import { z } from 'zod'

import { ChannelSettingsSchema } from './account.ts'
import { CampaignKindEnum, idSchema, msTimestampSchema } from './common.ts'
import { SeedExemplarSchema } from './seed-exemplar.ts'

// RAL-96 board A's "purpose" field — a fixed shortlist, same locked-set rationale as
// schemas/proposal.ts's RejectReasonEnum: lets copy speak to each purpose specifically
// ("your release window") instead of a free-text field nothing else can key off of.
export const CampaignPurposeEnum = z.enum(['tour_run', 'release', 'always_on'])
export type CampaignPurpose = z.infer<typeof CampaignPurposeEnum>

// RAL-52/RAL-96 rule #1: two real, selectable modes. 'manual' is the default everywhere a
// Campaign is created — automatic publishing is explicit opt-in, never earned or withheld.
export const PublishingModeEnum = z.enum(['manual', 'automatic'])
export type PublishingMode = z.infer<typeof PublishingModeEnum>

// The confidence threshold shown wherever a campaign hasn't been configured yet
// (agent-health.tsx, queue-list.tsx, inert-agent-status.tsx, publishing-fields.tsx).
export const DEFAULT_CONFIDENCE_THRESHOLD = 0.7

// RAL-217 lifecycle: draft -> scheduled -> active -> winding_down -> ended. One active per workspace.
export const CampaignStatusEnum = z.enum(['draft', 'scheduled', 'active', 'winding_down', 'ended'])
export type CampaignStatus = z.infer<typeof CampaignStatusEnum>

// What the previous active campaign does when this one activates.
export const CampaignHandoffEnum = z.enum(['wind_down', 'end'])
export type CampaignHandoff = z.infer<typeof CampaignHandoffEnum>

// Campaigns belong to a workspace and target a set of channels (accounts).
export const CampaignSchema = z.object({
  id: idSchema,
  workspaceId: idSchema,
  status: CampaignStatusEnum.default('draft'),
  handoff: CampaignHandoffEnum.default('wind_down'),
  channelIds: z.array(idSchema).default([]),
  includeUntaggedAssets: z.boolean().default(true),
  kind: CampaignKindEnum.default('creator'),
  name: z.string().min(1).default('My campaign'),
  purpose: CampaignPurposeEnum.default('always_on'),
  // Optional promotion window (e.g. a tour run or release cycle) — null on either end means
  // "no fixed end" (always_on's normal shape, and any purpose can leave it open).
  startDate: msTimestampSchema.nullable().default(null),
  endDate: msTimestampSchema.nullable().default(null),
  niche: z.string().min(1),
  voice: z.string().min(1),
  audience: z.string().min(1),
  mustUseHashtags: z.array(z.string()).default([]),
  bannedHashtags: z.array(z.string()).default([]),
  bannedTopics: z.array(z.string()).default([]),
  // RAL-96 board B: whether Rally may @-mention other accounts in a generated caption at all.
  mentionsAllowed: z.boolean().default(false),
  seedExemplars: z.array(SeedExemplarSchema).max(5).default([]),
  // Refined in PR 5 once the LLM prompt-context layer (packages/campaign's promotionContextSchema) lands.
  promotionContext: z.unknown().nullable().default(null),
  timezone: z.string().default('America/New_York'),
  confidenceThreshold: z.number().min(0).max(1).default(DEFAULT_CONFIDENCE_THRESHOLD),
  // RAL-52/RAL-96: the real manual/automatic toggle. Enforced in backend/services/agent/tick.ts's
  // computeTickResult — confidenceThreshold and the channel's postsBeforeAutoPublish only ever matter once this
  // is 'automatic'; a 'manual' campaign never auto-publishes regardless of confidence or ramp-up.
  publishingMode: PublishingModeEnum.default('manual'),
  kpi: z.string().default('reach'),
  // Admin-set per-campaign Bedrock model pin (RAL-38). null means "use Config.bedrockModelId".
  // modelOverrideBy/At are stamped together with modelOverride and cleared together with it.
  modelOverride: z.string().nullable().default(null),
  modelOverrideBy: z.string().nullable().default(null),
  modelOverrideAt: msTimestampSchema.nullable().default(null),
  // RAL-113's kill switch. Checked by the agent tick (before any candidate is scored) and the
  // publisher (before dispatch) — see backend/services/agent/rules.ts and
  // backend/services/publisher.ts. Orthogonal to RAL-52's not-yet-built manual/automatic mode
  // toggle: pausing stops publishing regardless of mode, so this doesn't wait on that ticket.
  paused: z.boolean().default(false),
  pausedAt: msTimestampSchema.nullable().default(null),
  // Snapshot of how many proposals the most recent pause cancelled — the paused shell's "what
  // pausing cost you" panel needs this to survive a page revisit, not just the pause response.
  pausedCancelledCount: z.number().int().nonnegative().default(0),
  createdAt: msTimestampSchema,
  updatedAt: msTimestampSchema
})

export type CampaignRow = z.infer<typeof CampaignSchema>
export type CampaignInput = z.input<typeof CampaignSchema>

// GET/PUT /api/campaign: the workspace campaign plus the active channel's settings, so the
// settings page keeps one form until the settings redesign splits them.
export const CampaignSettingsViewSchema = CampaignSchema.extend(ChannelSettingsSchema.shape)
export type CampaignSettingsView = z.infer<typeof CampaignSettingsViewSchema>

// ---- "Describe this campaign" autofill (RAL-220) -------------------------------------------

export const CampaignDraftRequestSchema = z.object({
  description: z.string().trim().min(1).max(1000)
})
export type CampaignDraftRequest = z.infer<typeof CampaignDraftRequestSchema>

// The only fields the model may set. Everything else in a draft is copied from the base campaign,
// so a hallucinated field can't leak pacing, trust, or pause state into the new campaign.
export const CampaignDraftLlmOutputSchema = z.object({
  name: z.string().trim().min(1).max(120),
  purpose: CampaignPurposeEnum,
  startDate: z.iso.date().nullable(),
  endDate: z.iso.date().nullable(),
  promotionContext: z.string().trim().max(2000),
  voice: z.string().trim().min(1).optional(),
  audience: z.string().trim().min(1).optional(),
  mustUseHashtags: z.array(z.string().trim().min(1)).max(20),
  kpi: z.string().trim().min(1)
})
export type CampaignDraftLlmOutput = z.infer<typeof CampaignDraftLlmOutputSchema>

// Create input minus the row identity and the runtime state a new campaign must never inherit
// (pause, admin model pin). niche/voice/audience may be blank on a first-run draft; the setup
// steps fill them before create.
export const CampaignDraftSchema = CampaignSchema.omit({
  id: true,
  workspaceId: true,
  status: true,
  createdAt: true,
  updatedAt: true,
  paused: true,
  pausedAt: true,
  pausedCancelledCount: true,
  modelOverride: true,
  modelOverrideBy: true,
  modelOverrideAt: true
}).extend({
  niche: z.string(),
  voice: z.string(),
  audience: z.string()
})
export type CampaignDraft = z.infer<typeof CampaignDraftSchema>

export const CampaignDraftFieldEnum = CampaignDraftSchema.keyof()
export type CampaignDraftField = z.infer<typeof CampaignDraftFieldEnum>

export const CampaignDraftResponseSchema = z.object({
  draft: CampaignDraftSchema,
  changedFields: z.array(CampaignDraftFieldEnum),
  fallback: z.boolean()
})
export type CampaignDraftResponse = z.infer<typeof CampaignDraftResponseSchema>

// ---- Lifecycle API (RAL-226) ---------------------------------------------------------------

export const CampaignSummarySchema = CampaignSchema.pick({ id: true, name: true, status: true })
export type CampaignSummary = z.infer<typeof CampaignSummarySchema>

export const CampaignListQuerySchema = z.object({
  status: CampaignStatusEnum.optional(),
  limit: z.coerce.number().int().positive().max(100).optional(),
  start: z.string().optional()
})
export type CampaignListQuery = z.infer<typeof CampaignListQuerySchema>

// Status is derived (draft, or scheduled when startDate is in the future); runtime state (pause,
// admin model pin) is never client-set.
export const CampaignCreateBodySchema = CampaignSchema.pick({
  channelIds: true,
  handoff: true,
  includeUntaggedAssets: true,
  kind: true,
  name: true,
  purpose: true,
  startDate: true,
  endDate: true,
  niche: true,
  voice: true,
  audience: true,
  mustUseHashtags: true,
  bannedHashtags: true,
  bannedTopics: true,
  mentionsAllowed: true,
  seedExemplars: true,
  promotionContext: true,
  timezone: true,
  confidenceThreshold: true,
  publishingMode: true,
  kpi: true
})
export type CampaignCreateBody = z.infer<typeof CampaignCreateBodySchema>

// Spelled out rather than CampaignCreateBodySchema.partial(): zod applies field defaults inside
// .partial(), which would reset every omitted field on a patch. `kind` is fixed at creation.
export const CampaignUpdateBodySchema = z.object({
  channelIds: z.array(idSchema).optional(),
  handoff: CampaignHandoffEnum.optional(),
  includeUntaggedAssets: z.boolean().optional(),
  name: z.string().min(1).optional(),
  purpose: CampaignPurposeEnum.optional(),
  startDate: msTimestampSchema.nullable().optional(),
  endDate: msTimestampSchema.nullable().optional(),
  niche: z.string().min(1).optional(),
  voice: z.string().min(1).optional(),
  audience: z.string().min(1).optional(),
  mustUseHashtags: z.array(z.string()).optional(),
  bannedHashtags: z.array(z.string()).optional(),
  bannedTopics: z.array(z.string()).optional(),
  mentionsAllowed: z.boolean().optional(),
  seedExemplars: z.array(SeedExemplarSchema).max(5).optional(),
  // Validated against PromotionContextSchema at the route and merged over the stored context.
  promotionContext: z.unknown().optional(),
  timezone: z.string().min(1).optional(),
  confidenceThreshold: z.number().min(0).max(1).optional(),
  publishingMode: PublishingModeEnum.optional(),
  kpi: z.string().min(1).optional()
})
export type CampaignUpdateBody = z.infer<typeof CampaignUpdateBodySchema>

// `handoff` is required by the route only when another campaign is active.
export const CampaignStartBodySchema = z.object({ handoff: CampaignHandoffEnum.optional() })
export type CampaignStartBody = z.infer<typeof CampaignStartBodySchema>

export const CampaignResponseSchema = z.object({ campaign: CampaignSchema })
export type CampaignResponse = z.infer<typeof CampaignResponseSchema>

export const CampaignListResponseSchema = z.object({
  campaigns: z.array(CampaignSchema),
  lastKey: z.string().optional()
})
export type CampaignListResponse = z.infer<typeof CampaignListResponseSchema>

export const CampaignStartResponseSchema = z.object({
  campaign: CampaignSchema,
  previous: CampaignSummarySchema.nullable()
})
export type CampaignStartResponse = z.infer<typeof CampaignStartResponseSchema>

// Omitted: the active campaign. Set: that campaign, when it is active or winding down.
export const CampaignHandoffPreviewQuerySchema = z.object({ campaignId: idSchema.optional() })
export type CampaignHandoffPreviewQuery = z.infer<typeof CampaignHandoffPreviewQuerySchema>

// Feeds the handoff prompt: "7 posts scheduled across 2 channels through Sep 20".
export const CampaignHandoffPreviewSchema = z.object({
  campaign: CampaignSummarySchema.nullable(),
  scheduledCount: z.number().int().nonnegative(),
  channelCount: z.number().int().nonnegative(),
  lastScheduledAt: msTimestampSchema.nullable()
})
export type CampaignHandoffPreview = z.infer<typeof CampaignHandoffPreviewSchema>

export const CampaignActivationOutcomeEnum = z.enum([
  'activated',
  'already_active',
  'not_found',
  'not_startable',
  'no_channels',
  'handoff_required',
  'busy'
])
export type CampaignActivationOutcome = z.infer<typeof CampaignActivationOutcomeEnum>
